import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { BlankSlate, Box, Spinner, useBreakpoint } from '@factorialco/deprecated-design-system'
import { ProductUpdatesPoliciesDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import ClickableMedia from 'modules/product_updates/components/ClickableMedia'
import { ModuleRelatedModulesCard } from 'modules/product_updates/components/ModuleRelatedModulesCard'
import UpsellProductDetail from 'modules/product_updates/components/UpsellProductDetail'
import { useGetProductUpdates } from 'modules/product_updates/hooks/useGetProductUpdates'
import Header from 'modules/product_updates/pages/ProductUpdateDetailPage/_Header'
import RelatedUpdatesCards from 'modules/product_updates/pages/ProductUpdateDetailPage/_RelatedUpdatesCards'
import { ProductUpdate } from 'modules/product_updates/types'

import StructuredDescription from './StructuredDescription'

type Props = {
  slug: string
}

const NewLayoutContent = ({ slug }: Props) => {
  const { loading, productUpdates } = useGetProductUpdates({ slug })
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const upsellOrigin = queryParams.get('upsell_origin')

  const { isMobile } = useBreakpoint()
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const productUpdate = productUpdates?.[0] as ProductUpdate | undefined
  const { data: upsellData } = useQuery(ProductUpdatesPoliciesDocument)
  const canSeeUpsellButton =
    upsellData?.productUpdates._policies.upsellButtonSeeUpsellButton.can ?? false
  const isModuleAcquired: boolean = useCheckModuleAcquired(productUpdate?.chargebeeModuleName || '')
  const isFeatureRelatedAcquired = useModuleAvailability(
    productUpdate?.featureRelated?.featureIdentifier || ''
  ).moduleAvailability.available

  useEffect(() => {
    if (loading || !productUpdate) return

    track(trackingEvents.productUpdates.visitProductUpdatesDetailPage, {
      slug,
      utm_campaign: queryParams.get('utm_campaign'),
      upsell_origin: queryParams.get('upsell_origin'),
    })
  }, [loading])

  if (loading)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )
  if (!productUpdate)
    return (
      <BlankSlate
        illustration="buildings"
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )

  const {
    thumbnail,
    shortDescription,
    productModule,
    releaseDate,
    title,
    chargebeeModuleName,
    longDescription,
    authorName,
    featureRelated,
    media,
  } = productUpdate

  const today = moment().startOf('day')
  const isAlreadyReleased = today.isSameOrAfter(releaseDate)

  const productUpdateMedia = (): { alt: string; url: string; video?: boolean }[] => {
    return (
      media?.map((item) => {
        const imgObj = {
          url: item.video?.mp4Url || item.url || '',
          alt: '',
          video: !!item.video?.mp4Url || item.url?.includes('.mp4'),
        }
        return imgObj
      }) || []
    )
  }

  const [video] = productUpdateMedia()

  // Upsell Logic
  const isProductUpdateAcquired =
    (isModuleAcquired || isFeatureRelatedAcquired || !chargebeeModuleName) && canSeeUpsellButton
  // End of Upsell Logic

  return (
    <Box className="max-w-full" alignSelf="center" justifyContent="center">
      <Box alignSelf="center" maxWidth="full" width="full">
        <Header
          title={title}
          shortDescription={shortDescription}
          isAlreadyReleased={isAlreadyReleased}
          releaseDate={releaseDate}
          authorName={authorName || ''}
          productModule={productModule}
        />
        <Box flexDirection={isMobile ? 'column' : 'row'} width="full" gap="s16" paddingX="s16">
          <Box gap="s16" width="full" maxWidth="s600">
            <Box width="full">
              <ClickableMedia src={!!video ? video.url : thumbnail.src} video={!!video} autoPlay>
                <Box
                  borderRadius={{ all: 'abs016' }}
                  overflowX="hidden"
                  alignSelf="center"
                  width="full"
                >
                  {video ? (
                    <video
                      src={video.url || ''}
                      title={video?.alt || ''}
                      autoPlay
                      loop
                      muted
                    ></video>
                  ) : (
                    <img src={thumbnail.src} style={{ width: '100%', display: 'block' }} />
                  )}
                </Box>
              </ClickableMedia>
            </Box>

            <UpsellProductDetail
              isAlreadyReleased={isAlreadyReleased}
              canSeeUpsellButton={canSeeUpsellButton ?? false}
              productModule={productModule}
              chargebeeModuleName={chargebeeModuleName || ''}
              productUpdateSlug={slug}
              featureIdentifier={featureRelated?.featureIdentifier}
              productUpdateId={productUpdate.id.toString()}
              source={`From product news module_availability_banner ${slug}${
                upsellOrigin ? ` - ${upsellOrigin}` : ''
              }`}
            />
            <Box className="text-lg text-f1-foreground-secondary">{shortDescription}</Box>
            <Box className="">
              {longDescription && <StructuredDescription content={longDescription} />}
            </Box>
            <ModuleRelatedModulesCard
              productUpdate={productUpdate}
              isProductUpdateAcquired={isProductUpdateAcquired ?? false}
              source={`From product news recommended_module ${slug}${
                upsellOrigin ? ` - ${upsellOrigin}` : ''
              }`}
            />
          </Box>
          <RelatedUpdatesCards productUpdate={productUpdate} />
        </Box>
      </Box>
    </Box>
  )
}

export default NewLayoutContent
